import './addTransaction.css';
import React, {Component} from "react";
import {
    PostExpenseRequestDto, UserDto, BasicUserDto
} from "../model/functionModels";
import TransactionService from "../services/transactionService";
import {Multiselect} from "multiselect-react-dropdown";
import DatePicker from "react-datepicker";
import CurrencyInput from "react-currency-input-field";
import Button from "./button";
import i18nService from "../../i18n/i18nService";

interface AddExpenseState {
    targets: UserRepresentation[],
    date: Date,
    details: string,
    amount: string,
    counter: number,
    useCustomSplit: boolean,
    customSplits: {[userId: string]: string}, // Maps userId to amount string (e.g., "10.50")
    isSubmitting: boolean,
}

interface AddExpenseProps {
    loggedInUser: UserDto,
    possibleUsers: (UserDto | BasicUserDto)[],
    onFailure: (a: string) => Promise<any>,
    onRedirection: (newUrl: string) => Promise<any>,
    refreshPageContent: any,
}

interface UserRepresentation {
    name: string,
    id: string
}

class AddExpense extends Component<AddExpenseProps, AddExpenseState> {

    constructor(props: AddExpenseProps) {
        super(props, {})
        this.handleAddExpense = this.handleAddExpense.bind(this);
        this.formatDate = this.formatDate.bind(this);
        this.state = {
            date: new Date(),
            targets: [],
            details: '',
            amount: '',
            counter: 1,
            useCustomSplit: false,
            customSplits: {},
            isSubmitting: false,
        };
    }

    async handleAddExpense() {
        if (this.state.isSubmitting) {
            return;
        }
        this.setState({isSubmitting: true});
        try {
            const date = this.formatDate(this.state.date!);
            const amount = Math.floor(parseFloat(this.state.amount || "0") * 100);
            if (!amount || isNaN(amount)) {
                await this.props.onFailure(i18nService.t('dashboard.expenseForm.invalidAmount'));
                this.setState({isSubmitting: false});
                return;
            }
            if (!this.state.targets || this.state.targets.length === 0) {
                await this.props.onFailure(i18nService.t('dashboard.expenseForm.selectAtLeastOne'));
                this.setState({isSubmitting: false});
                return;
            }

        // Custom split is optional (default is even split). If enabled, it must sum to total.
        
        // Build userSplits if custom split is enabled
        let userSplits: {[userId: string]: number} | undefined = undefined;
        if (this.state.useCustomSplit) {
            userSplits = {};
            let totalSplit = 0;
            for (const target of this.state.targets) {
                const splitAmountStr = this.state.customSplits[target.id] || "0";
                const splitAmount = Math.floor(parseFloat(splitAmountStr) * 100);
                if (isNaN(splitAmount) || splitAmount < 0) {
                    await this.props.onFailure(i18nService.t('dashboard.expenseForm.invalidSplitAmount').replace('{{name}}', target.name));
                    this.setState({isSubmitting: false});
                    return;
                }
                userSplits[target.id] = splitAmount;
                totalSplit += splitAmount;
            }
            if (totalSplit !== amount) {
                await this.props.onFailure(i18nService.t('dashboard.expenseForm.splitMustSumToTotal')
                    .replace('{{splitTotal}}', (totalSplit / 100).toFixed(2))
                    .replace('{{total}}', (amount / 100).toFixed(2)));
                this.setState({isSubmitting: false});
                return;
            }
        }
        
            await TransactionService.createExpense(
            {
                authorUserId: this.props.loggedInUser.id,
                targets: this.state.targets!.map(u => u.id),
                date: date,
                details: this.state.details!,
                amount: amount,
                userSplits: userSplits,
            } as PostExpenseRequestDto,
            async () => {
                // we clear the expense form
                this.setState({
                    date: new Date(),
                    targets: [],
                    details: '',
                    amount: '',
                    counter: this.state.counter+1,
                    useCustomSplit: false,
                    customSplits: {},
                    isSubmitting: false,
                });
                // we update the transactions list and the debt map
                await this.props.refreshPageContent();
            },
            async (e) => {
                this.setState({isSubmitting: false});
                await this.props.onFailure(e);
            },
            this.props.onRedirection);
        } catch (_e) {
            this.setState({isSubmitting: false});
        }
    }

    formatDate(date: Date) : string {
        return `${date.getFullYear()}-${(date.getMonth() + 1) > 9 ? "" : "0"}${date.getMonth() + 1}-${date.getDate() > 9 ? "" : "0"}${date.getDate()}`;
    }

    render() {
        return this.props.possibleUsers ? (
            <div className="AddTransaction-Wrapper">
                <div key="addTransactionUsers" className="AddTransaction-UserWhoIsPaidFor">
                    <Multiselect
                        key={"Multiselect-expenseFor-"+this.state.counter}
                        placeholder={i18nService.t('dashboard.expenseForm.whoIsFor')}
                        options={this.props.possibleUsers.map(u => {
                            return {name: `${u.firstName} ${u.lastName}`, id: u.id} as UserRepresentation
                        })}
                        selectedValues={this.state.targets}
                        onSelect={(event) => {
                            this.setState({
                                targets: event
                            })
                        }
                        }
                        onRemove={(event) => {
                            this.setState({
                                targets: event
                            })
                        }
                        }
                        displayValue="name"
                    />
                </div>
                <div className="AddTransaction-Date" key="addTransactionDate">
                    <DatePicker
                        dateFormat="dd-MM-yyyy"
                        selected={this.state.date}
                        monthsShown={2}
                        onChange={(date: Date) => {
                            this.setState({
                                date: date
                            });
                        }
                        }
                        placeholderText={i18nService.t('dashboard.expenseForm.dateOfTransaction')}
                        maxDate={new Date()}
                    />
                </div>
                <div className="AddTransaction-Details">
                    <input key="addTransactionDetails"
                           type="text" placeholder={i18nService.t('dashboard.expenseForm.expenseDetails')} value={this.state.details}
                           onChange={(event) => {
                               this.setState({
                                   details: event.target.value
                               });
                           }
                           }/>
                </div>
                <div className="AddTransaction-Amount" key="addTransactionMoney">
                    <CurrencyInput
                        id="moneyInput"
                        name="moneyInput"
                        placeholder={i18nService.t('dashboard.expenseForm.transactionAmount')}
                        value={this.state.amount}
                        decimalsLimit={2}
                        intlConfig={{
                            currency: "EUR",
                            locale: "en-MT",
                        }}
                        onValueChange={(value, name) => {
                            this.setState({
                                amount: value!
                            });
                        }
                        }
                    />
                </div>

                <div className="AddTransaction-CustomSplit">
                    <div className="AddTransaction-SplitToggleRow">
                        <div className="AddTransaction-SplitToggleText">
                            <div className="AddTransaction-SplitToggleLabel">{i18nService.t('dashboard.expenseForm.unevenSplit')}</div>
                            <div className="AddTransaction-SplitToggleSubLabel">{i18nService.t('dashboard.expenseForm.unevenSplitDescription')}</div>
                        </div>
                        <label className="AddTransaction-ToggleSwitch" aria-label="Uneven split">
                            <input
                                type="checkbox"
                                checked={this.state.useCustomSplit}
                                onChange={() => {
                                    const next = !this.state.useCustomSplit;
                                    // When enabling custom split, prefill an even split (including remainder cents) across selected targets
                                    if (next) {
                                        const amountCents = Math.floor(parseFloat(this.state.amount || "0") * 100);
                                        const targets = (this.state.targets || []).map(t => t.id).filter(Boolean);
                                        const uniqueTargets = Array.from(new Set(targets)).sort();
                                        const splits: {[userId: string]: string} = {...this.state.customSplits};
                                        if (amountCents > 0 && uniqueTargets.length > 0) {
                                            const base = Math.floor(amountCents / uniqueTargets.length);
                                            const remainder = amountCents % uniqueTargets.length;
                                            uniqueTargets.forEach((id, idx) => {
                                                const cents = base + (idx < remainder ? 1 : 0);
                                                splits[id] = (cents / 100).toFixed(2);
                                            });
                                        } else {
                                            uniqueTargets.forEach((id) => {
                                                if (!splits[id]) {
                                                    splits[id] = "0.00";
                                                }
                                            });
                                        }
                                        this.setState({useCustomSplit: true, customSplits: splits});
                                    } else {
                                        this.setState({useCustomSplit: false});
                                    }
                                }}
                            />
                            <span className="AddTransaction-ToggleSlider"></span>
                        </label>
                    </div>

                    {this.state.useCustomSplit && (
                        <div className="AddTransaction-CustomSplitInputs">
                            {(this.state.targets || []).map((t) => (
                                <div className="AddTransaction-CustomSplitRow" key={`split-${t.id}`}>
                                    <label>{t.name}</label>
                                    <div>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            placeholder="0.00"
                                            value={this.state.customSplits[t.id] ?? ""}
                                            onChange={(e) => {
                                                this.setState({
                                                    customSplits: {
                                                        ...this.state.customSplits,
                                                        [t.id]: e.target.value,
                                                    },
                                                });
                                            }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <Button value={this.state.isSubmitting ? i18nService.t('dashboard.expenseForm.adding') : i18nService.t('dashboard.expenseForm.addExpense')} onClick={this.handleAddExpense} enabled={!this.state.isSubmitting}/>
            </div>
        ) : <div className="AddTransaction-Wrapper-Empty"/>;
    }
}

export default AddExpense;

